import { gql } from '@apollo/client';

/* ── the MF Alpha engine ────────────────────────────────────── */

/**
 * Every rate and return on this schema is ALREADY a percentage — the engine
 * computes `x / y * 100` and `index - 100` before it answers. Nothing read from
 * these documents is ever multiplied by 100. The one exception is the
 * `consistency` signal's raw value, which is genuinely a 0-1 share.
 *
 * A null index field means NOT MEASURED, never zero.
 */

/**
 * One ranked fund. `signals` is the engine's own breakdown of *why* the fund
 * ranks where it does — each signal's raw value, its z-score against the
 * category and the weight it carries — which is what turns a rank into an
 * explanation.
 *
 * The four forward fields are null on a live ranking, because no future has
 * happened yet; they populate only when the caller asked for a past `asOfDate`.
 * `forwardExcess` is the one that carries information — a fund and its category
 * move together, so a raw −8.8% in a falling market may still have beaten its
 * peers.
 */
const RANKED_FUND = `
  securitySymbol
  schemeName
  fundHouse
  category
  horizon
  rank
  categorySize
  percentile
  score
  coverage
  benchmarkType
  benchmarkSymbols
  asOfDate
  forwardReturn
  categoryForwardReturn
  forwardExcess
  beatCategory
  signals {
    name
    description
    raw
    zscore
    weight
    higherIsBetter
  }
`;

export const ALPHA_RANKING = gql`
  query AlphaRanking($category: String!, $horizon: String!, $limit: Int = 25) {
    mfAlphaCategoryRanking(category: $category, horizon: $horizon, limit: $limit) { ${RANKED_FUND} }
  }
`;

/**
 * Top-ranked funds per category for one horizon.
 *
 * `topFraction` and `perCategory` are alternative selection rules and the
 * server takes one or the other — 0.25 is the fraction the stored backtests
 * measured, so moving it detaches the list from its evidence. With no
 * `asOfDate` this is today's live ranking and answers in milliseconds; with a
 * past one it re-ranks as of that date and measures forward, taking ~30s.
 */
export const ALPHA_IDEAS = gql`
  query AlphaIdeas(
    $horizon: String!
    $topFraction: Float
    $perCategory: Int
    $categories: [String!]
    $asOfDate: String
  ) {
    mfAlphaIdeas(
      horizon: $horizon
      topFraction: $topFraction
      perCategory: $perCategory
      categories: $categories
      asOfDate: $asOfDate
    ) {
      horizon
      asOfDate
      evidence {
        horizon
        benchmarkType
        windowFrom
        windowTo
        hitRate
        baseRate
        lift
        picks
        meanExcessPicks
        meanExcessUniverse
        runId
        createdAt
      }
      categories {
        category
        benchmarkType
        funds { ${RANKED_FUND} }
      }
    }
  }
`;

export const ALPHA_FOR_FUND = gql`
  query AlphaForFund($securitySymbol: String!) {
    mfAlphaForFund(securitySymbol: $securitySymbol) { ${RANKED_FUND} }
  }
`;

/** Rank an explicit set of schemes — a client's holdings, or the compare tray. */
export const ALPHA_RANK_UNIVERSE = gql`
  query AlphaRankUniverse($securitySymbols: [String!]!, $horizon: String!, $limit: Int) {
    mfAlphaRankUniverse(securitySymbols: $securitySymbols, horizon: $horizon, limit: $limit) {
      ${RANKED_FUND}
    }
  }
`;

/**
 * Engine freshness, and the weight sets the rankings were built from.
 *
 * `weights` is what makes a rank defensible: `source` reading anything other
 * than `default_prior` means a fitted set was activated, which is not the
 * intended posture, and the out-of-sample lift beside it is how that call gets
 * checked.
 */
export const ALPHA_STATUS = gql`
  query AlphaStatus {
    mfAlphaStatus {
      totalRows
      horizons
      latestAsOfDate
      latestNavDate
      isStale
      categoriesCovered { horizon category fundsRanked asOfDate }
      weights {
        horizon
        benchmarkType
        source
        isActive
        weights
        fittedFrom
        fittedTo
        inSampleLift
        validatedFrom
        validatedTo
        outOfSampleLift
        outOfSampleHitRate
        outOfSampleBaseRate
        notes
      }
    }
  }
`;

/** The evidence log, newest first. Also the run picker's data source. */
export const ALPHA_BACKTEST_RUNS = gql`
  query AlphaBacktestRuns($horizon: String, $runType: String, $limit: Int) {
    mfAlphaBacktestRuns(horizon: $horizon, runType: $runType, limit: $limit) {
      id
      runType
      horizon
      benchmarkType
      windowFrom
      windowTo
      topFraction
      hitRate
      baseRate
      lift
      picks
      meanExcessPicks
      meanExcessUniverse
      notes
      createdAt
      weights
    }
  }
`;

/**
 * One run and everything it produced. The whole backtest screen is driven from
 * this single document on purpose: every panel then describes the same set of
 * rankings and the page cannot contradict itself.
 *
 * Two different measurements live in here and must never be blended. `hitRate`
 * / `baseRate` / `lift` come from overlapping full-horizon windows and answer
 * "how often did a pick beat its benchmark"; `returnsSummary` and `periods`
 * come from non-overlapping rebalance periods and answer "what did the
 * portfolio earn".
 *
 * The three cost series are likewise not interchangeable. A gross CAGR quoted
 * beside a category CAGR overstates the edge; an after-tax strategy compared
 * against a pre-tax benchmark is rigged the other way, which is why
 * `categoryAfterTaxCagr` exists as the like-for-like comparator.
 *
 * And every `*Excess*` field here — on the summary and on each category — is a
 * WHOLE-WINDOW total, never an annual rate, even though the CAGRs beside it
 * are. On the 2020→2026 run `afterTaxExcessVsCategory` is −31.278, which is
 * 126.921 − 158.199 in total return; the same gap per year is −2.6. See
 * COST_SERIES in lib/alpha.js, which keeps the two apart.
 */
const BACKTEST_DETAIL = `
  id
  runType
  horizon
  benchmarkType
  windowFrom
  windowTo
  topFraction
  entryLagDays
  notes
  createdAt
  weights
  hitRate
  baseRate
  lift
  picks
  universeObservations
  meanExcessPicks
  meanExcessUniverse
  returnsSummary {
    periodsCount
    years
    strategyTotalReturn
    strategyCagr
    strategyNetTotalReturn
    strategyNetCagr
    strategyAfterTaxTotalReturn
    strategyAfterTaxCagr
    exitLoadDragCagr
    taxDragCagr
    categoryTotalReturn
    categoryCagr
    categoryAfterTaxTotalReturn
    categoryAfterTaxCagr
    excessVsCategory
    netExcessVsCategory
    afterTaxExcessVsCategory
    indexTotalReturn
    indexCagr
    excessVsIndex
    indexedCategories
  }
  periods {
    date
    endDate
    nPicks
    strategyReturn
    strategyNetReturn
    strategyAfterTaxReturn
    exitLoadCost
    taxCost
    categoryReturn
    indexReturn
    strategyIndex
    strategyNetIndex
    strategyAfterTaxIndex
    categoryIndex
    indexIndex
  }
  perCategory {
    category
    hitRate
    baseRate
    lift
    picks
    periods
    strategyTotalReturn
    categoryTotalReturn
    indexTotalReturn
    strategyCagr
    categoryCagr
    indexCagr
    excessVsCategory
    excessVsIndex
  }
  signalContributions {
    name
    description
    weight
    liftWithout
    contribution
  }
  decay {
    lagDays
    lift
    hitRate
    baseRate
  }
  pickContributions {
    date
    category
    securitySymbol
    schemeName
    rank
    score
    weight
    fundReturn
    categoryReturn
    excessReturn
  }
`;

export const ALPHA_BACKTEST_DETAIL = gql`
  query AlphaBacktestDetail($runId: Int, $horizon: String, $benchmarkType: String) {
    mfAlphaBacktestDetail(runId: $runId, horizon: $horizon, benchmarkType: $benchmarkType) {
      ${BACKTEST_DETAIL}
    }
  }
`;

/**
 * Forward returns after a PAST ranking — the strongest validation surface here.
 *
 * It re-ranks nothing: `rank` is the model's own placing as it stood on
 * `asOfDate` and this only adds what happened next. Every eligible fund comes
 * back, not just the picks, because the unpicked funds are the control group —
 * without them a list of winners is equally consistent with a good ranking and
 * with a rising market.
 *
 * `byLag` holds the same horizon bought 0 / 2 / 5 / 10 / 15 trading days later.
 * The ranking is identical at every lag; only the buy date moves, so any
 * difference between them is decay and nothing else.
 *
 * Takes ~30 seconds, so it is fetched on demand rather than on mount. `urlId`
 * is the scorecard's handle for the agent's report (see RESULT_HEAD in
 * queries/attribution.js).
 */
export const ALPHA_SCORECARD = gql`
  query AlphaScorecard(
    $asOfDate: String!
    $horizon: String!
    $categories: [String!]
    $topFraction: Float
  ) {
    mfAlphaScorecard(
      asOfDate: $asOfDate
      horizon: $horizon
      categories: $categories
      topFraction: $topFraction
    ) {
      success
      urlId
      reason
      asOfDate
      measuredTo
      horizon
      entryLagDays
      lagsMeasured
      delayImpact { lagDays hitRate baseRate lift meanExcessPicks }
      summary { funds picks hitRate baseRate lift meanExcessPicks meanExcessAll }
      funds {
        securitySymbol
        schemeName
        fundHouse
        category
        rank
        categorySize
        isPick
        score
        coverage
        forwardReturn
        categoryForwardReturn
        excessReturn
        beatCategory
        byLag { lagDays forwardReturn categoryForwardReturn excessReturn beatCategory }
        signals { name raw zscore higherIsBetter }
      }
    }
  }
`;
